/**
 * Bundle 86 - Admin Event Survey Analytics Panel rewired against the
 * canonical Bundle 84.1 rollup endpoint.
 *
 *   GET /api/admin/events/{event_id}/survey/rollup-84_1
 *
 * Renders the 10 canonical tiles the spec calls for:
 *   1. event_rating (avg + 1..5 histogram)
 *   2. comparison_to_other_events (6-bucket bar)
 *   3. games_rating (avg + "did not participate" separated)
 *   4. future_event_likelihood (5-option bar, NOT stars)
 *   5. vibe_words top 8 chip cloud
 *   6. future_event_formats top 5
 *   7. self_reported_discovery_source top 8
 *   8. connection_metrics (romantic %, friendship %, direct exchange %,
 *      successful_connection_participants count)
 *   9. missed_connection_requests_count
 *  10. publishable_testimonials_count (link to moderation queue)
 *
 * No em/en dashes, no eslint-disable, no photo/image alt strings.
 * Reuses Bundle 62 loading skeleton + retry banner patterns.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Users, MessageSquare, Compass, ArrowRight, RefreshCw, AlertCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------
const Tile = ({ label, value, sub, testId, tone = 'default' }) => (
  <Card>
    <CardContent className="p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">{label}</div>
      <div className={`text-2xl font-display font-bold ${tone === 'primary' ? 'text-primary-900' : 'text-primary-800'}`}>{value}</div>
      {sub ? <div className="text-xs text-neutral-500 mt-0.5">{sub}</div> : null}
    </CardContent>
  </Card>
);

const HorizontalBar = ({ rows, testId, emptyText = 'No responses yet.' }) => {
  const total = rows.reduce((a, b) => a + (b.count || 0), 0) || 1;
  if (!rows.length) return <div className="text-xs text-neutral-500" data-testid={`${testId}-empty`}>{emptyText}</div>;
  return (
    <div className="space-y-1" data-testid={testId}>
      {rows.map((r) => {
        const pct = Math.round(((r.count || 0) * 100) / total);
        return (
          <div key={r.key || r.label} className="flex items-center gap-2 text-xs">
            <span className="w-32 shrink-0 text-primary-800 truncate" title={r.label}>{r.label}</span>
            <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
              <div className="h-full bg-secondary-500" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-8 text-right text-neutral-600">{r.count || 0}</span>
          </div>
        );
      })}
    </div>
  );
};

const ChipCloud = ({ rows, testId }) => {
  if (!rows.length) return <div className="text-xs text-neutral-500">Nothing yet.</div>;
  const max = Math.max(...rows.map((r) => r.count || 0), 1);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {rows.map((r) => {
        const weight = (r.count || 0) / max;
        return (
          <Badge
            key={r.key}
            variant="outline"
            className="bg-neutral-50 text-primary-900 border-neutral-200"
            style={{ fontWeight: 500 + Math.round(weight * 300) }}
            data-testid={`${testId}-${String(r.key).toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
          >
            {r.key} <span className="ml-1 text-[10px] text-neutral-500">x{r.count}</span>
          </Badge>
        );
      })}
    </div>
  );
};

const SkeletonBar = ({ n = 5 }) => (
  <div className="space-y-1.5" data-testid="analytics-skeleton">
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} className="flex items-center gap-2">
        <div className="w-32 h-2 rounded bg-neutral-100" />
        <div className="flex-1 h-2 rounded bg-neutral-100" />
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Static label lookups (matches the survey question copy)
// ---------------------------------------------------------------------------
const COMPARISON_LABELS = {
  first: 'This was my first',
  much_better: 'Much better than others',
  somewhat_better: 'Somewhat better',
  about_the_same: 'About the same',
  somewhat_worse: 'Somewhat worse',
  much_worse: 'Much worse',
};
const LIKELIHOOD_LABELS = {
  definitely: 'Definitely',
  probably: 'Probably',
  maybe: 'Maybe',
  probably_not: 'Probably not',
  definitely_not: 'Definitely not',
};
// Bundle 88.1 - canonical 12-option discovery labels (matches the
// ATTENDEE_QUESTIONS options exactly, plus a few legacy fallbacks so
// pre-88.1 responses that slipped past the rollup remap still render
// with a friendly name instead of the raw key.
const DISCOVERY_LABELS = {
  friend_family: 'Friend/Family Member',
  whatsapp_group: 'WhatsApp Group',
  instagram_post: 'Instagram Post',
  instagram_ad: 'Instagram Ad',
  facebook_post: 'Facebook Post',
  facebook_ad: 'Facebook Ad',
  facebook_group: 'Facebook Group',
  search: 'Search',
  podcast_radio: 'Podcast/Radio',
  another_event: 'Another Event',
  flyer: 'Flyer',
  other: 'Other',
  // Legacy fallbacks (already remapped server-side).
  instagram: 'Instagram Post',
  tiktok: 'Other',
  linkedin: 'Other',
  google_search: 'Search',
  podcast: 'Podcast/Radio',
  press_article: 'Other',
  community_group: 'Other',
  influencer_creator: 'Other',
  sms_text: 'Other',
};

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------
const AdminEventSurveyAnalyticsPanel = () => {
  // Bundle 86.1 - gate every fetch on auth being fully hydrated.
  // Without this the effect can fire immediately after a tab switch
  // before the axios interceptor has the token attached, producing
  // a first-mount 401 even for a valid super_admin session.
  const { loading: authLoading, token, user } = useAuth();
  const authReady = !authLoading && (!!token || !!user);
  const [events, setEvents] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [data, setData] = useState(null);
  // Bundle 123 - rollup-102 connection funnel numbers.
  const [rollup102, setRollup102] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [pendingRetry, setPendingRetry] = useState(false);

  // Bundle 182 Task 1 - persist per-admin toggle in localStorage
  // so each super-admin's preference sticks across sessions.
  const _adminKey = (user?.email || 'anon').toLowerCase();
  const _lsKey = `b182:likelihood_include_excluded:${_adminKey}`;
  const [includeExcluded, setIncludeExcludedState] = useState(() => {
    try {
      return window.localStorage.getItem(_lsKey) === 'true';
    } catch (e) { return false; }
  });
  const setIncludeExcluded = (v) => {
    setIncludeExcludedState(v);
    try { window.localStorage.setItem(_lsKey, v ? 'true' : 'false'); }
    catch (e) { /* localStorage blocked */ }
  };

  useEffect(() => {
    if (!authReady) return;
    if (eventsLoaded) return;
    setEventsLoaded(true);
    api.get('/admin/analytics/event-survey-index')
      .then((r) => {
        const list = r.data.events || [];
        setEvents(list);
        if (!selectedId && list.length) {
          setSelectedId(list[0].id);
        }
      })
      .catch(() => {});
  }, [authReady, eventsLoaded, selectedId]);

  const loadOne = useCallback(async (id, { isRetry = false } = {}) => {
    if (!id) return;
    if (!authReady) {
      setPendingRetry(true);
      return;
    }
    setBusy(true);
    setLoadError(null);
    try {
      const r = await api.get(
        `/admin/events/${id}/survey/rollup-84_1`,
        { params: { refresh: 1,
                       include_excluded: includeExcluded } },
      );
      setData(r.data);
      // Bundle 123 - additionally fetch rollup-102 (connection
      // funnel). Non-blocking: analytics panel still renders if
      // this second call fails.
      try {
        const r2 = await api.get(
          `/admin/events/${id}/survey/rollup-102/connections`,
        );
        setRollup102(r2.data);
      } catch (e2) {
        setRollup102(null);
      }
      setPendingRetry(false);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 && !isRetry) {
        setPendingRetry(true);
        setBusy(false);
        return;
      }
      setData(null);
      setLoadError(
        e?.response?.data?.detail || 'Could not load survey analytics.',
      );
      setPendingRetry(false);
    }
    setBusy(false);
  }, [authReady, includeExcluded]);

  useEffect(() => {
    if (!authReady) return;
    loadOne(selectedId);
  }, [authReady, selectedId, loadOne, includeExcluded]);

  // Bundle 86.1 - one-shot retry after auth becomes ready.
  useEffect(() => {
    if (!authReady || !pendingRetry || !selectedId) return;
    loadOne(selectedId, { isRetry: true });
  }, [authReady, pendingRetry, selectedId, loadOne]);

  const ratingDistRows = useMemo(() => {
    const dist = data?.event_rating?.distribution || [];
    return [5, 4, 3, 2, 1].map((s) => {
      const found = dist.find((d) => d.score === s) || {};
      return { key: String(s), label: `${s} stars`, count: found.count || 0 };
    });
  }, [data]);

  const comparisonRows = useMemo(() => {
    const buckets = data?.comparison_to_other_events?.buckets || [];
    const wanted = ['first', 'much_better', 'somewhat_better',
      'about_the_same', 'somewhat_worse', 'much_worse'];
    return wanted.map((k) => {
      const found = buckets.find((b) => b.key === k);
      return { key: k, label: COMPARISON_LABELS[k] || k, count: found?.count || 0 };
    });
  }, [data]);

  const gamesRows = useMemo(() => {
    const buckets = data?.games_rating?.buckets || [];
    const scored = ['5', '4', '3', '2', '1'].map((s) => {
      const found = buckets.find((b) => String(b.key) === s);
      return { key: s, label: `${s} stars`, count: found?.count || 0 };
    });
    return scored;
  }, [data]);

  const gamesAvg = useMemo(() => {
    const buckets = data?.games_rating?.buckets || [];
    let n = 0, sum = 0;
    for (const b of buckets) {
      const k = String(b.key);
      const num = Number.parseInt(k, 10);
      if (!Number.isNaN(num) && num >= 1 && num <= 5) {
        n += b.count || 0;
        sum += num * (b.count || 0);
      }
    }
    return n ? (Math.round((sum / n) * 100) / 100) : null;
  }, [data]);

  const gamesNaCount = useMemo(() => {
    const buckets = data?.games_rating?.buckets || [];
    return (buckets.find((b) => String(b.key) === 'na')?.count) || 0;
  }, [data]);

  const likelihoodRows = useMemo(() => {
    const buckets = data?.future_event_likelihood?.buckets || [];
    const wanted = ['definitely', 'probably', 'maybe',
      'probably_not', 'definitely_not'];
    return wanted.map((k) => {
      const found = buckets.find((b) => b.key === k);
      return { key: k, label: LIKELIHOOD_LABELS[k], count: found?.count || 0 };
    });
  }, [data]);

  const vibeRows = data?.vibe_words?.top || [];
  const formatRows = (data?.future_event_formats?.top || []).map((r) => ({
    key: r.key, label: r.key, count: r.count,
  }));
  const discoveryRows = (data?.self_reported_discovery_source?.top || [])
    .map((r) => ({ key: r.key, label: DISCOVERY_LABELS[r.key] || r.key, count: r.count }));

  const conn = data?.connection_metrics || {};
  const totalResponses = data?.response_count || 0;
  const romanticPct = totalResponses
    ? Math.round(((conn.romantic_intent_count || 0) * 100) / totalResponses)
    : 0;
  const friendshipPct = totalResponses
    ? Math.round(((conn.friendship_intent_count || 0) * 100) / totalResponses)
    : 0;
  const directExchangePct = totalResponses
    ? Math.round(((conn.direct_contact_exchange_yes_count || 0) * 100) / totalResponses)
    : 0;

  const isEmpty = !!data && totalResponses === 0;
  const isAwaitingAuth = !authReady;

  return (
    <div className="space-y-4" data-testid="admin-analytics-survey-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-xs text-neutral-500 uppercase tracking-widest">Event</label>
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="border border-neutral-200 rounded px-2 py-1 text-sm bg-white min-w-[260px]"
          data-testid="analytics-event-select"
        >
          {events.map((e) => (
            <option key={e.id} value={e.id}>{e.title || e.id}</option>
          ))}
        </select>
        <Button
          size="sm"
          variant="outline"
          onClick={() => loadOne(selectedId)}
          disabled={busy}
          data-testid="analytics-survey-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <div className="ml-auto flex items-center gap-3
                              text-[11px] text-neutral-500">
          <span data-testid="analytics-rollup-source">
            Source: rollup-84_1 (canonical survey fields, excludes
            test + preview responses)
          </span>
          {data?.last_computed_at ? (
            <span data-testid="analytics-rollup-last-computed">
              As of{' '}
              <time dateTime={data.last_computed_at}>
                {new Date(data.last_computed_at).toLocaleString()}
              </time>
              {typeof data.cache_age_seconds === 'number'
                && data.cache_age_seconds > 300 ? (
                  <span className="ml-2 text-amber-700 font-semibold"
                              data-testid="analytics-stale-warning">
                    stale, click Refresh
                  </span>
                ) : null}
            </span>
          ) : null}
        </div>
      </div>

      {isAwaitingAuth ? (
        <div className="rounded-md border border-neutral-200 bg-white px-3 py-2 text-xs text-neutral-600" data-testid="analytics-awaiting-auth">
          Waiting for your session to hydrate...
        </div>
      ) : null}

      {loadError ? (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center justify-between gap-2"
          role="alert"
          data-testid="analytics-load-error"
        >
          <span className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {loadError}
          </span>
          <Button
            size="sm"
            onClick={() => loadOne(selectedId)}
            className="bg-amber-700 text-white hover:bg-amber-800 shrink-0"
            data-testid="analytics-load-retry"
          >Retry</Button>
        </div>
      ) : null}

      {busy && !data ? (
        <div className="grid gap-3 md:grid-cols-4" data-testid="analytics-loading">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}><CardContent className="p-4"><SkeletonBar n={3} /></CardContent></Card>
          ))}
        </div>
      ) : null}

      {data && isEmpty ? (
        <Card><CardContent className="p-6 text-center" data-testid="analytics-empty-state">
          <div className="text-primary-900 font-semibold">No survey responses yet.</div>
          <div className="text-neutral-500 text-xs mt-1">
            As guests reflect, their ratings and words will show up here.
          </div>
        </CardContent></Card>
      ) : null}

      {data && !isEmpty ? (
        <>
          {/* Tile 1 - Event rating + 1..5 histogram */}
          <div className="grid gap-3 md:grid-cols-4">
            <Tile
              label="Event rating"
              tone="primary"
              value={data.event_rating?.average != null ? data.event_rating.average : '-'}
              sub={`${data.event_rating?.count || 0} rated (of ${totalResponses} responses)`}
              testId="tile-event-rating"
            />
            <Tile
              label="Missed-connection requests"
              value={data.missed_connection_requests_count || 0}
              sub="from Step 3 of the canonical survey"
              testId="tile-missed-connections"
            />
            <Tile
              label="Publishable testimonials"
              value={data.publishable_testimonials_count || 0}
              sub={<Link
                to="/admin/testimonials"
                className="inline-flex items-center gap-1 text-secondary-800 hover:text-secondary-900"
                data-testid="analytics-testimonials-jump"
              >Open moderation queue <ArrowRight className="h-3 w-3" /></Link>}
              testId="tile-publishable-testimonials"
            />
            <Card><CardContent className="p-4" data-testid="tile-rating-distribution">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                Rating distribution
              </div>
              <HorizontalBar rows={ratingDistRows} testId="rating-distribution-rows" />
            </CardContent></Card>
          </div>

          {/* Tiles 2, 4 - Comparison to other events + future event likelihood */}
          <div className="grid gap-3 md:grid-cols-2">
            <Card><CardContent className="p-4" data-testid="tile-comparison">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                Compared to other matchmaking events
              </div>
              <HorizontalBar rows={comparisonRows} testId="comparison-rows" />
            </CardContent></Card>
            <Card><CardContent className="p-4" data-testid="tile-future-likelihood">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                Likelihood to attend future events
              </div>
              <HorizontalBar rows={likelihoodRows} testId="future-likelihood-rows" />
              {/* Bundle 182 - Show excluded toggle (default OFF).
                  Preference is persisted per admin in localStorage
                  so each admin's choice sticks across sessions. */}
              <div
                className="mt-3 pt-2 border-t border-neutral-100 flex items-center gap-2 flex-wrap"
                data-testid="likelihood-include-excluded-row"
              >
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-primary-800"
                    checked={includeExcluded}
                    onChange={(e) => setIncludeExcluded(e.target.checked)}
                    data-testid="likelihood-include-excluded-toggle"
                  />
                  <span className="text-[11px] text-primary-900 font-semibold">
                    Show excluded
                  </span>
                </label>
                <span className="text-[10px] text-neutral-500">
                  By default excluded ratings are hidden. Enable to see
                  the full response set.
                </span>
                {(data?.event_rating?.excluded_count || 0) > 0 ? (
                  <span
                    className="text-[10px] text-rose-800"
                    data-testid="likelihood-excluded-count"
                  >
                    ({data?.event_rating?.excluded_count} excluded
                    {includeExcluded ? ', shown' : ', hidden'})
                  </span>
                ) : null}
              </div>
            </CardContent></Card>
          </div>

          {/* Tile 3 - Games rating + did-not-participate separated */}
          <div className="grid gap-3 md:grid-cols-2">
            <Card><CardContent className="p-4" data-testid="tile-games-rating">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                  Games or activities
                </div>
                <div className="text-2xl font-display font-bold text-primary-900" data-testid="games-rating-avg">
                  {gamesAvg != null ? gamesAvg : '-'}
                </div>
              </div>
              <HorizontalBar rows={gamesRows} testId="games-rating-rows" />
              <div className="text-[11px] text-neutral-500 mt-2" data-testid="games-rating-na">
                Did not participate: <strong>{gamesNaCount}</strong>
              </div>
            </CardContent></Card>

            {/* Tile 5 - Vibe words chip cloud */}
            <Card><CardContent className="p-4" data-testid="tile-vibe-words">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                Vibe words (top 8)
              </div>
              <ChipCloud rows={vibeRows} testId="vibe-words-cloud" />
            </CardContent></Card>
          </div>

          {/* Tiles 6, 7 - Future formats + self-reported discovery source */}
          <div className="grid gap-3 md:grid-cols-2">
            <Card><CardContent className="p-4" data-testid="tile-future-formats">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                Requested future formats (top 5)
              </div>
              <HorizontalBar rows={formatRows} testId="future-formats-rows" emptyText="No preferences yet." />
            </CardContent></Card>
            <Card><CardContent className="p-4" data-testid="tile-discovery-source">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-1">
                <Compass className="h-3 w-3" /> Self-reported discovery source
              </div>
              <HorizontalBar rows={discoveryRows} testId="discovery-rows" emptyText="Not asked yet." />
              <div className="text-[10px] text-neutral-400 mt-2">
                Self-reported. Never overwrites UTM attribution.
              </div>
            </CardContent></Card>
          </div>

          {/* Tile 8 - Connection metrics */}
          <Card><CardContent className="p-4" data-testid="tile-connection-metrics">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-1">
              <Users className="h-3 w-3" /> Connections
            </div>
            <div className="grid gap-3 md:grid-cols-4">
              <Tile
                label="Romantic intent"
                value={`${romanticPct}%`}
                sub={`${conn.romantic_intent_count || 0} of ${totalResponses}`}
                testId="tile-connection-romantic"
              />
              <Tile
                label="Friendship intent"
                value={`${friendshipPct}%`}
                sub={`${conn.friendship_intent_count || 0} of ${totalResponses}`}
                testId="tile-connection-friendship"
              />
              <Tile
                label="Direct exchange (yes)"
                value={`${directExchangePct}%`}
                sub={`${conn.direct_contact_exchange_yes_count || 0} of ${totalResponses}`}
                testId="tile-connection-direct-exchange"
              />
              <Tile
                label="Successful connection participants"
                value={conn.successful_connection_participants || 0}
                sub={`${conn.facilitated_intro_pairs || 0} facilitated pairs`}
                testId="tile-connection-successful"
              />
            </div>
            <div className="text-[10px] text-neutral-400 mt-2">
              {conn.dedup_rule || ''}
            </div>
          </CardContent></Card>

          {/* Bundle 123 - rollup-102 connection funnel. Sits below
              rollup-84_1 tiles so admins see the full pipeline from
              survey submission -> facilitated intro -> mutual
              acceptance -> contact exchange -> success story. */}
          {rollup102 ? (
            <Card data-testid="rollup-102-tile-group">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      Connection funnel (rollup-102)
                    </div>
                    <div className="text-primary-900 font-semibold">
                      Introductions, follow-ups, and stories
                    </div>
                  </div>
                  {rollup102.last_computed_at ? (
                    <div className="text-[10px] text-neutral-500"
                          data-testid="rollup-102-last-computed">
                      As of{' '}
                      <time dateTime={rollup102.last_computed_at}>
                        {new Date(rollup102.last_computed_at)
                          .toLocaleString()}
                      </time>
                    </div>
                  ) : null}
                </div>
                <div className="grid gap-3 md:grid-cols-4">
                  <Tile label="Romantic"
                          value={rollup102.counters?.romantic_connection_respondents || 0}
                          sub="respondents"
                          testId="tile-102-romantic" />
                  <Tile label="Friendship / dost"
                          value={rollup102.counters?.friendship_dost_respondents || 0}
                          sub="respondents"
                          testId="tile-102-friendship" />
                  <Tile label="Direct-exchange respondents"
                          value={rollup102.counters?.direct_exchange_respondents || 0}
                          sub={`${rollup102.counters?.direct_connection_participants || 0} unique participants`}
                          testId="tile-102-direct-exchange" />
                  <Tile label="Intro requests submitted"
                          value={rollup102.counters?.introduction_requests_submitted || 0}
                          sub={`${rollup102.counters?.targets_identified || 0} targets identified`}
                          testId="tile-102-intro-submitted" />
                </div>
                <div className="grid gap-3 md:grid-cols-4">
                  <Tile label="Admin calls completed"
                          value={rollup102.counters?.admin_calls_completed || 0}
                          sub="outcome recorded"
                          testId="tile-102-calls-completed" />
                  <Tile label="Awaiting digital consent"
                          value={rollup102.counters?.awaiting_digital_consent || 0}
                          sub="target hasn't decided yet"
                          testId="tile-102-awaiting-consent" />
                  <Tile label="Mutually accepted"
                          value={rollup102.counters?.mutually_accepted_introductions || 0}
                          sub="both parties consented"
                          testId="tile-102-mutually-accepted" />
                  <Tile label="Declined / withdrawn / expired"
                          value={rollup102.counters?.declined_withdrawn_expired || 0}
                          sub="closed without exchange"
                          testId="tile-102-declined" />
                </div>
                <div className="grid gap-3 md:grid-cols-4">
                  <Tile label="Contact exchanges completed"
                          value={rollup102.counters?.contact_exchanges_completed || 0}
                          sub="details shared"
                          testId="tile-102-contact-exchanges" />
                  <Tile label="Successful connection participants"
                          tone="primary"
                          value={rollup102.counters?.successful_connection_participants || 0}
                          sub={`${rollup102.counters?.confirmed_connection_pairs || 0} confirmed pairs`}
                          testId="tile-102-successful-participants" />
                  <Tile label="Follow-ups sent"
                          value={rollup102.counters?.followups_sent || 0}
                          sub={`${rollup102.counters?.followups_positive || 0} positive replies`}
                          testId="tile-102-followups-sent" />
                  <Tile label="Success story candidates"
                          value={rollup102.counters?.success_story_candidates || 0}
                          sub="ready for review"
                          testId="tile-102-story-candidates" />
                </div>
                <div className="text-[10px] text-neutral-400">
                  Excludes: {(rollup102.exclusions_applied || []).join(', ')}
                </div>
              </CardContent>
            </Card>
          ) : null}

          <div className="text-[10px] text-neutral-400 flex items-center gap-1 justify-end" data-testid="analytics-generated-at">
            <MessageSquare className="h-3 w-3" />
            Rollup generated at {data.generated_at}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default AdminEventSurveyAnalyticsPanel;
